import { sanitizeCountryCode } from "@expert/common-ui";
import { Indicator, Popover } from "@mantine/core";
import { type JSX, useMemo } from "react";

import { ScheduleCallbackPanel } from "@/call-controls/ScheduleCallback/components/CallbackForm/ScheduleCallbackPanel";
import { IconButton, Schedule } from "@/common-ui";
import { usePartnerConfig, usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import { useCallbackState } from "@/sdk";

import { useControlsStore } from "../controls.store";

interface CallbackPanelButtonProps {
    disabledTooltip?: string;
    disabled?: boolean;
}

export function ToggleCallbackPanelButton({ disabledTooltip, disabled }: CallbackPanelButtonProps): JSX.Element {
    const {
        features: { defaultCountry = "US", isInternationalPhoneEnabled = false },
    } = usePartnerConfig();
    const { callbackEnabled } = usePartnerFeatureConfigured();
    const sanitizedDefaultCountry = sanitizeCountryCode(defaultCountry);
    const callbackState = useCallbackState();

    const hasCallback = !!callbackState;
    const scheduleCallbackPanelOpen = useControlsStore((state) => state.scheduleCallbackPanelOpen);

    const canScheduleCallback: boolean = useMemo(
        () => scheduleCallbackPanelOpen && !hasCallback,
        [hasCallback, scheduleCallbackPanelOpen],
    );
    const handleClick = scheduleCallbackPanelOpen
        ? useControlsStore.getState().closeCallbackPanel
        : useControlsStore.getState().openCallbackPanel;

    const scheduledForText: string | null = useMemo(() => {
        if (!callbackState) return null;

        if (callbackState.callbackType === "CallbackNow" && callbackState.callbackDelay !== null) {
            if (callbackState.callbackDelay <= 1) return "immediately after the call ends";
            return `${callbackState.callbackDelay} seconds after the call ends`;
        }

        const scheduledTime = new Date(callbackState.scheduledFor);
        const formattedDate = new Intl.DateTimeFormat(navigator.languages[0], {
            dateStyle: "short",
            timeStyle: "short",
        }).format(scheduledTime);

        return `${formattedDate}`;
    }, [callbackState]);

    const getDisabledTooltip = () => {
        if (disabled && disabledTooltip) return disabledTooltip;
        if (disabled && !disabledTooltip) return "Callbacks are not supported for this call";
        if (hasCallback) return `A callback is scheduled for ${scheduledForText}`;

        return "Schedule callback";
    };
    const tooltip = getDisabledTooltip();

    return (
        <Popover
            opened={scheduleCallbackPanelOpen}
            position="bottom"
            zIndex="var(--mantine-priority-highest)"
            styles={{
                dropdown: {
                    backgroundColor: "transparent",
                    border: "none",
                    padding: 0,
                },
            }}
        >
            <Popover.Target>
                <Indicator disabled={!hasCallback} size={8} offset={2}>
                    <IconButton
                        active={canScheduleCallback}
                        analytics={{
                            actionId: "ScheduleCallbackButton",
                        }}
                        aria-label="Schedule callback"
                        data-testid="call-controls-callback-schedule-begin"
                        onClick={handleClick}
                        radius="xl"
                        size="30"
                        tooltip={tooltip}
                        disabled={disabled}
                    >
                        <Schedule
                            height={15}
                            stroke={canScheduleCallback ? "var(--mantine-color-black)" : "var(--mantine-color-white)"}
                        />
                    </IconButton>
                </Indicator>
            </Popover.Target>
            <Popover.Dropdown>
                {callbackEnabled && scheduleCallbackPanelOpen ? (
                    <ScheduleCallbackPanel
                        isInternationalPhoneEnabled={isInternationalPhoneEnabled}
                        defaultCountry={sanitizedDefaultCountry}
                    />
                ) : null}
            </Popover.Dropdown>
        </Popover>
    );
}
